/**
 * Shared SheetJS XLSX→CSV conversion options — the single source of truth so the
 * frontend interactive importer and the backend upload/job paths convert Excel
 * workbooks identically (a divergence in date/number rendering would make the
 * preview and the persisted import disagree).
 *
 * `dateNF` is the format for genuine DATE-typed cells, but it is NOT sufficient
 * on its own: SheetJS only applies `dateNF` to date cells WITHOUT their own
 * number format. A cell that carries an explicit cell-level format (`z`) and/or
 * a cached formatted text (`w`) keeps rendering with THAT format, so the emitted
 * CSV date shape depends on the exporting user's Excel locale (Bit2Me's Spanish
 * exports emit `01-01-2022 12:00:04` — dd-mm-yyyy). Call
 * `normalizeWorksheetDateCellFormats()` on every worksheet BEFORE
 * `sheet_to_csv`/`sheet_to_json` to make the output deterministic. The value is
 * kept time-preserving (`hh:mm:ss`, 24-hour) rather than `yyyy-mm-dd` so a date
 * cell never silently collapses to midnight.
 */

/** SheetJS date number-format for date-typed cells (24-hour, time-preserving). */
export const XLSX_DATE_NF = 'yyyy-mm-dd hh:mm:ss'

/**
 * Options for `XLSX.utils.sheet_to_csv(worksheet, ...)`. Used by the frontend
 * `readFileAsCsvWithFormat` and the backend `convertXlsxToCsv` so both emit the
 * same CSV text.
 */
export const XLSX_SHEET_TO_CSV_OPTIONS = {
  blankrows: false,
  rawNumbers: false,
  dateNF: XLSX_DATE_NF,
} as const

/**
 * Structural view of a SheetJS cell — only the fields this helper touches.
 * Typed structurally so @bat/shared does NOT depend on any xlsx library.
 *
 * - `t`: cell type tag (`'d'` = date under `cellDates: true`)
 * - `v`: underlying value (a `Date` instance for date cells)
 * - `w`: cached formatted text rendered by the EXPORTING app's locale format
 * - `z`: cell-level number format string
 */
interface XlsxDateLikeCell {
  t?: unknown
  v?: unknown
  w?: string
  z?: string
}

/**
 * Force every native date-typed cell of a worksheet to render with our
 * deterministic `XLSX_DATE_NF` format, regardless of the workbook's locale.
 *
 * WHY this is needed (tax-critical): `dateNF` in the conversion options only
 * applies to date cells WITHOUT their own number format. When a workbook's
 * cells carry an explicit locale format (cell-level `z`) — and/or a cached
 * formatted text `w` — SheetJS keeps THAT rendering, so the CSV date shape
 * depends on the exporting user's Excel locale. Real-world failure: Bit2Me
 * Spanish-locale xlsx exports render `01-01-2022 12:00:04` (dd-mm-yyyy); the
 * downstream strict `YYYY-MM-DD HH:mm:ss` parser then fails and the loose
 * fallback misreads `03-10-2022` as US MM-DD (month/day swapped for days ≤12,
 * dropped rows for days >12).
 *
 * Deleting `w` and setting `z = XLSX_DATE_NF` makes a later
 * `sheet_to_csv`/`sheet_to_json` re-render the date deterministically.
 * Call this on EVERY worksheet right after obtaining it from the workbook.
 */
export function normalizeWorksheetDateCellFormats(worksheet: Record<string, unknown>): void {
  for (const [cellAddress, cellValue] of Object.entries(worksheet)) {
    // Keys starting with '!' are sheet metadata (!ref, !margins, ...), not cells.
    if (cellAddress.startsWith('!')) {
      continue
    }
    if (typeof cellValue !== 'object' || cellValue === null) {
      continue
    }
    const cell = cellValue as XlsxDateLikeCell
    // `t === 'd'` covers cellDates:true reads; `v instanceof Date` is
    // defense-in-depth for any path that materializes Date values differently.
    const isNativeDateCell = cell.t === 'd' || cell.v instanceof Date
    if (!isNativeDateCell) {
      continue
    }
    delete cell.w
    cell.z = XLSX_DATE_NF
  }
}
